/**
 * Pure TikTok Content Posting API request-shape helpers (TikTok photo-mode
 * task, roadmap format-breadth item). Same extraction pattern as
 * `shared/instagram-container-params.ts` — lives in `shared/` (not
 * `supabase/functions/_shared/`) because this repo's `test:unit:ci`
 * (`vitest.config.ts`) EXCLUDES `supabase/**` entirely, so any pure logic
 * that needs a real Vitest gate has to live outside that tree and get
 * re-exported through a `_shared/` stub for the edge function to import
 * (same "structure it testably" compromise Task A8 used for Instagram).
 *
 * Verified against TikTok's official Content Posting API docs (2026-07-11):
 *   - https://developers.tiktok.com/doc/content-posting-api-reference-photo-post
 *   - https://developers.tiktok.com/doc/content-posting-api-reference-direct-post
 *   - https://developers.tiktok.com/doc/content-posting-api-get-started
 *   - https://developers.tiktok.com/doc/content-posting-api-reference-get-video-status
 *
 * Photo posts use a DIFFERENT init endpoint than video —
 * `POST /v2/post/publish/content/init/`, NOT the video-only
 * `/v2/post/publish/video/init/` `tiktok-client.ts#initPost` already calls —
 * and require explicit top-level `media_type`/`post_mode` fields the video
 * endpoint doesn't take at all. `source_info.source` is `PULL_FROM_URL`
 * only (Mora's media already lives in public Supabase Storage, same public
 * URL the video path already fetches) — TikTok requires the DEVELOPER APP to
 * have verified ownership of the URL's domain/prefix for `PULL_FROM_URL` to
 * work at all (an account-level TikTok Developer Portal setting, not
 * something a per-request field can express — flagged as a deploy
 * prerequisite in the phase report, not something this module can check).
 *
 * The status-poll endpoint (`POST /v2/post/publish/status/fetch/`) is
 * IDENTICAL for photo and video publish_ids (confirmed via the get-video-
 * status doc — same `status`/`publicaly_available_post_id` response shape),
 * so `tiktok-client.ts#checkStatusDetailed`/`waitForCompletion` need no
 * photo-specific variant; only the init call differs.
 *
 * UNAUDITED-CLIENT CAVEAT (corrected per pre-merge review finding #1,
 * 2026-07-11): TikTok does NOT silently downgrade an unaudited client's
 * post to private — it REJECTS the init call outright (HTTP 403,
 * error.code `unaudited_client_can_only_post_to_private_accounts` or
 * `privacy_level_option_mismatch`, per the reference docs' Error Codes
 * tables) when the requested `privacy_level` is not among the options the
 * creator-info endpoint returns for this client+creator. The correct flow
 * (per the get-started + direct-post reference docs) is: query
 * `POST /v2/post/publish/creator_info/query/` first, then send a
 * `privacy_level` picked FROM the returned `privacy_level_options` — for an
 * unaudited client that list is limited to private options (SELF_ONLY), so
 * posts publish but may be limited to private (SELF_ONLY) visibility until
 * TikTok audit approval. `selectTikTokPrivacyLevel` below implements the
 * pick; `TikTokApiClient#queryCreatorInfo` does the query; the readiness
 * layer surfaces the visibility caveat as the non-blocking
 * `tiktok_unaudited_may_publish_private` warning. No audit-status record
 * was found anywhere in this repo/docs for Mora's TikTok app, so it's
 * treated as UNAUDITED.
 */

export const TIKTOK_PHOTO_MAX_IMAGES = 35;
/** post_info.title max length for PHOTO posts, in UTF-16 "runes" (code
 * units). Doc-verified 2026-11-03. Video captions are much longer
 * (`TIKTOK_VIDEO_TITLE_MAX_RUNES`). */
export const TIKTOK_PHOTO_TITLE_MAX_RUNES = 90;
/** post_info.title max length for VIDEO posts: the title IS the caption, up
 * to 2,200 UTF-16 runes, hashtags and mentions parsed (direct-post reference,
 * verified 2026-11-03). Mora cut it at 150 until 2026-11-04. */
export const TIKTOK_VIDEO_TITLE_MAX_RUNES = 2200;
/** post_info.description max length for PHOTO posts, in UTF-16 runes. */
export const TIKTOK_PHOTO_DESCRIPTION_MAX_RUNES = 4000;

export type TikTokPrivacyLevel =
  'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY';

export const TIKTOK_PRIVACY_LEVELS: readonly TikTokPrivacyLevel[] = [
  'PUBLIC_TO_EVERYONE',
  'MUTUAL_FOLLOW_FRIENDS',
  'FOLLOWER_OF_CREATOR',
  'SELF_ONLY',
];

export type TikTokDeliveryMode = 'direct' | 'drafts';

/**
 * What the creator chose in the editor's TikTok settings card, stored at
 * `social_posts.media_keys.perPlatform.tiktok` so it is captured when the post
 * is scheduled and snapshotted per target. The server never trusts it: every
 * read goes through `normalizeTikTokSettings`.
 */
export interface TikTokPostSettings {
  /** `direct` posts to the profile; `drafts` sends the post to the TikTok
   * inbox, where the creator finishes it and can pick a sound. */
  deliveryMode: TikTokDeliveryMode;
  /** No default: `null` until the creator picks one (audit rule). */
  privacyLevel: TikTokPrivacyLevel | null;
  allowComment: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  /** Photo posts only: ask TikTok to add a recommended track. */
  autoAddMusic: boolean;
  /** Photo posts only: 0-based index of the slide used as the cover
   * (`photo_cover_index`). The order sent to TikTok is the editor's order. */
  photoCoverIndex: number;
  /** The commercial-content switch. Both boxes below only count while on. */
  commercialContent: boolean;
  yourBrand: boolean;
  brandedContent: boolean;
  /** The creator confirmed the Music Usage Confirmation and consent line. */
  consent: boolean;
}

export const DEFAULT_TIKTOK_POST_SETTINGS: TikTokPostSettings = {
  deliveryMode: 'direct',
  privacyLevel: null,
  allowComment: false,
  allowDuet: false,
  allowStitch: false,
  autoAddMusic: true,
  photoCoverIndex: 0,
  commercialContent: false,
  yourBrand: false,
  brandedContent: false,
  consent: false,
};

function isTikTokPrivacyLevel(value: unknown): value is TikTokPrivacyLevel {
  return typeof value === 'string' && (TIKTOK_PRIVACY_LEVELS as readonly string[]).includes(value);
}

/**
 * Turns whatever sits in `perPlatform.tiktok` into a full settings object.
 * Unknown keys are dropped; missing booleans take the audit defaults (comment,
 * duet and stitch off; music on). Returns `null` only when nothing was stored,
 * so callers can tell "never configured" from "configured with defaults".
 */
export function normalizeTikTokSettings(raw: unknown): TikTokPostSettings | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const bool = (key: string, fallback: boolean) =>
    typeof r[key] === 'boolean' ? (r[key] as boolean) : fallback;
  return {
    deliveryMode: r.deliveryMode === 'drafts' ? 'drafts' : 'direct',
    privacyLevel: isTikTokPrivacyLevel(r.privacyLevel) ? r.privacyLevel : null,
    allowComment: bool('allowComment', DEFAULT_TIKTOK_POST_SETTINGS.allowComment),
    allowDuet: bool('allowDuet', DEFAULT_TIKTOK_POST_SETTINGS.allowDuet),
    allowStitch: bool('allowStitch', DEFAULT_TIKTOK_POST_SETTINGS.allowStitch),
    autoAddMusic: bool('autoAddMusic', DEFAULT_TIKTOK_POST_SETTINGS.autoAddMusic),
    photoCoverIndex:
      typeof r.photoCoverIndex === 'number' && Number.isFinite(r.photoCoverIndex)
        ? Math.max(0, Math.floor(r.photoCoverIndex))
        : 0,
    commercialContent: bool('commercialContent', false),
    yourBrand: bool('yourBrand', false),
    brandedContent: bool('brandedContent', false),
    consent: bool('consent', false),
  };
}

/** The two disclosure flags TikTok takes, derived so a box only counts while
 * the commercial-content switch is on. */
export function tiktokBrandFlags(settings: TikTokPostSettings | null | undefined): {
  brand_organic_toggle: boolean;
  brand_content_toggle: boolean;
} {
  const on = settings?.commercialContent === true;
  return {
    brand_organic_toggle: on && settings?.yourBrand === true,
    brand_content_toggle: on && settings?.brandedContent === true,
  };
}

export interface TikTokPhotoPostInfo {
  title: string;
  description: string;
  privacy_level: TikTokPrivacyLevel;
  disable_comment: boolean;
  /** DIRECT_POST only: TikTok adds recommended music; the creator can change
   * it in TikTok. There is no field to name a specific sound. */
  auto_add_music: boolean;
  /**
   * Commercial-content disclosure. The photo-post reference lists these as
   * required booleans for DIRECT_POST, so both are always sent explicitly.
   */
  brand_content_toggle: boolean;
  brand_organic_toggle: boolean;
}

export interface TikTokPhotoSourceInfo {
  source: 'PULL_FROM_URL';
  photo_images: string[];
  photo_cover_index: number;
}

/** The exact body for `POST /v2/post/publish/content/init/` (media_type PHOTO). */
export interface TikTokPhotoInitRequestBody {
  media_type: 'PHOTO';
  post_mode: 'DIRECT_POST';
  post_info: TikTokPhotoPostInfo;
  source_info: TikTokPhotoSourceInfo;
  /** Top-level on the photo endpoint (not inside post_info, unlike video):
   * labels the photo post "AI-generated". */
  is_aigc: boolean;
}

/**
 * Photo "send to drafts": `post_mode: MEDIA_UPLOAD` puts the photos in the
 * creator's TikTok inbox. They finish the post in TikTok's editor, where they
 * can pick a sound. Only `title` and `description` carry over; privacy,
 * interactions, music and disclosure are chosen in TikTok. Needs `video.upload`
 * and TikTok app 31.8 or later.
 */
export interface TikTokPhotoDraftRequestBody {
  media_type: 'PHOTO';
  post_mode: 'MEDIA_UPLOAD';
  post_info: { title: string; description: string };
  source_info: TikTokPhotoSourceInfo;
  is_aigc: boolean;
}

export interface TikTokPhotoPostParamsInput {
  /** 1-35 public image URLs already normalised to what TikTok accepts (see
   * `shared/image-normalization-rules.ts`: JPEG, 1080 px, under 20 MB). */
  photoUrls: string[];
  /** The post's full caption text — same `content` field the video path
   * derives its title from. Split across title/description below so the
   * photo post keeps the FULL caption (description, 4000-rune cap) rather
   * than truncating to the shorter 90-rune title field the way a
   * title-only mapping would. */
  caption: string;
  /** Dispatcher-provided explicit title override, mirroring the video
   * path's `title` param convention in `post-to-tiktok/index.ts`. Falls
   * back to `caption` when absent/blank. */
  title?: string | null;
  /** MUST come from the creator-info query's `privacy_level_options` (see
   * `selectTikTokPrivacyLevel`) — TikTok 403s
   * (`privacy_level_option_mismatch`) on any level not in that list. */
  privacyLevel?: TikTokPrivacyLevel;
  /** 0-based index into `photoUrls` used as the carousel cover. Defaults to
   * the first image; out-of-range values are clamped into
   * `[0, photoUrls.length - 1]` (review finding #3) rather than sent
   * unvalidated to TikTok. NOTE: the dispatcher's `coverImageUrl` (an
   * auto-extracted VIDEO poster-frame URL) is NOT applicable to TikTok
   * photo posts — TikTok's photo cover is index-based only, so no
   * URL→index mapping is attempted; a cover choice can only arrive here as
   * an explicit index. Documented, not silent. */
  coverIndex?: number;
  /** Photo music: TikTok picks a recommended track. Defaults to the stored
   * setting, else on, because a photo post with no music is silent. */
  autoAddMusic?: boolean;
  /** Label the post as AI-generated (`is_aigc`). */
  isAIGC?: boolean;
  /** Creator-chosen interactions and disclosure, already normalised by
   * `normalizeTikTokSettings`. */
  settings?: TikTokPostSettings | null;
}

/**
 * Picks the `privacy_level` for a TikTok init call.
 *
 * - The creator's own choice wins when TikTok offers it (creator_info
 *   `privacy_level_options`). TikTok 403s any level outside that list
 *   (`privacy_level_option_mismatch`); it never silently downgrades.
 * - With no choice, or a choice that is not offered, the answer is the most
 *   private option on offer, never PUBLIC by default.
 * - With no options at all (the creator_info call failed) the choice is passed
 *   through unchecked, or SELF_ONLY when there is none. `initWithPrivateFallback`
 *   retries once as SELF_ONLY if TikTok then rejects it as unaudited.
 */
export function resolveTikTokPrivacyLevel(
  chosen: TikTokPrivacyLevel | null | undefined,
  options: readonly string[] | null | undefined,
): TikTokPrivacyLevel {
  const list = Array.isArray(options)
    ? options.filter((option): option is string => typeof option === 'string' && option.length > 0)
    : [];
  if (list.length === 0) return chosen ?? 'SELF_ONLY';
  if (chosen && list.includes(chosen)) return chosen;
  if (list.includes('SELF_ONLY')) return 'SELF_ONLY';
  return list[0] as TikTokPrivacyLevel;
}

/** The most private offered option; see `resolveTikTokPrivacyLevel`. */
export function selectTikTokPrivacyLevel(
  options: readonly string[] | null | undefined,
): TikTokPrivacyLevel {
  return resolveTikTokPrivacyLevel(null, options);
}

const TIKTOK_VIDEO_MIMES = ['video/mp4', 'video/quicktime', 'video/webm'] as const;
export type TikTokVideoMime = (typeof TIKTOK_VIDEO_MIMES)[number];

/**
 * The real `Content-Type` for a video upload. TikTok accepts MP4, MOV and WebM;
 * the PUT must carry the file's own type, not always `video/mp4`. The storage
 * response header wins when it names one of the three, then the file extension,
 * then MP4 (the format TikTok recommends).
 */
export function tiktokVideoMimeFor(
  url: string,
  contentTypeHeader?: string | null,
): TikTokVideoMime {
  const header = (contentTypeHeader ?? '').split(';')[0].trim().toLowerCase();
  const fromHeader = TIKTOK_VIDEO_MIMES.find((mime) => mime === header);
  if (fromHeader) return fromHeader;
  const path = url.split(/[?#]/)[0].toLowerCase();
  if (path.endsWith('.mov')) return 'video/quicktime';
  if (path.endsWith('.webm')) return 'video/webm';
  return 'video/mp4';
}

export const TIKTOK_PRIVATE_ONLY_WARNING = 'private_only_unaudited_app';
export const TIKTOK_PRIVATE_ONLY_MESSAGE =
  "TikTok posted this as private (only you can see it). Mora's TikTok connection can only post privately until TikTok approves it.";

/**
 * The warning a direct post carries when TikTok put it out as private although the author asked for
 * a wider audience (an unaudited app can only post private). One rule for the live path
 * (`post-to-tiktok`) and the late settle (the reconcile), so a post reads the same either way.
 * `requested` unknown counts as stepped down, as before.
 */
export function tiktokPrivateOnlyWarning(
  requested: string | null | undefined,
  effective: string | null | undefined,
): { warning: typeof TIKTOK_PRIVATE_ONLY_WARNING; warningMessage: string } | null {
  if (effective !== 'SELF_ONLY') return null;
  if (requested === 'SELF_ONLY') return null;
  return { warning: TIKTOK_PRIVATE_ONLY_WARNING, warningMessage: TIKTOK_PRIVATE_ONLY_MESSAGE };
}

/** The share link for a post: photo posts live under `/photo/`, videos under `/video/`. */
export function tiktokPermalinkFor(
  username: string,
  kind: 'video' | 'photo',
  postId: string,
): string {
  return `https://www.tiktok.com/@${username}/${kind}/${postId}`;
}

export function clampRunes(value: string, max: number): string {
  // TikTok's documented limits are in UTF-16 "runes" (code units) — the same
  // unit JS string .length/.slice already operate on, so no surrogate-pair-
  // aware truncation is needed beyond what .slice does today. Mirrors the
  // existing video-path precedent (`derivedTitle.slice(0, 150)` in
  // post-to-tiktok/index.ts).
  return value.slice(0, max);
}

/**
 * The video `post_info.title`: TikTok's caption field. Always the post's own
 * caption when there is one (that is what the editor preview shows), else an
 * explicit dispatcher title; clamped to 2,200 runes, never 150.
 */
export function tiktokVideoTitleFor(input: {
  caption?: string | null;
  title?: string | null;
}): string {
  const caption = typeof input.caption === 'string' ? input.caption : '';
  const title = typeof input.title === 'string' ? input.title : '';
  const raw = caption.trim().length > 0 ? caption : title;
  return clampRunes(raw, TIKTOK_VIDEO_TITLE_MAX_RUNES);
}

/**
 * Builds the exact request body for
 * `POST https://open.tiktokapis.com/v2/post/publish/content/init/` when
 * publishing a TikTok photo/carousel post. Pure — no network, no Deno/Node-
 * specific APIs — so it's directly unit-testable, same contract as
 * `instagram-container-params.ts#containerParamsFor`.
 */
export function tiktokPhotoInitRequestBodyFor(
  input: TikTokPhotoPostParamsInput,
): TikTokPhotoInitRequestBody {
  const rawTitle =
    typeof input.title === 'string' && input.title.trim().length > 0
      ? input.title
      : (input.caption ?? '');

  const settings = input.settings ?? null;
  const autoAddMusic = input.autoAddMusic ?? settings?.autoAddMusic ?? true;

  return {
    media_type: 'PHOTO',
    post_mode: 'DIRECT_POST',
    post_info: {
      title: clampRunes(rawTitle, TIKTOK_PHOTO_TITLE_MAX_RUNES),
      description: clampRunes(input.caption ?? '', TIKTOK_PHOTO_DESCRIPTION_MAX_RUNES),
      privacy_level: input.privacyLevel ?? 'SELF_ONLY',
      disable_comment: settings ? !settings.allowComment : true,
      auto_add_music: autoAddMusic,
      ...tiktokBrandFlags(settings),
    },
    source_info: {
      source: 'PULL_FROM_URL',
      photo_images: input.photoUrls,
      // Clamp into [0, length-1] (review finding #3) — an out-of-range or
      // fractional index is corrected rather than sent unvalidated.
      photo_cover_index: Math.min(
        Math.max(0, Math.floor(input.coverIndex ?? settings?.photoCoverIndex ?? 0)),
        Math.max(0, input.photoUrls.length - 1),
      ),
    },
    is_aigc: input.isAIGC === true,
  };
}

/** Request body for a photo post sent to the creator's TikTok drafts. */
export function tiktokPhotoDraftRequestBodyFor(
  input: TikTokPhotoPostParamsInput,
): TikTokPhotoDraftRequestBody {
  const direct = tiktokPhotoInitRequestBodyFor(input);
  return {
    media_type: 'PHOTO',
    post_mode: 'MEDIA_UPLOAD',
    post_info: {
      title: direct.post_info.title,
      description: direct.post_info.description,
    },
    source_info: direct.source_info,
    is_aigc: direct.is_aigc,
  };
}

/**
 * Validation error message, or `null` if `photoUrls` fits TikTok's photo-post
 * constraints (1-35 PULL_FROM_URL images). The format is not checked here: the
 * publisher converts every photo to JPEG from its real bytes. Pure; the caller
 * (`post-to-tiktok/index.ts`) throws it as a `PostingError`, and readiness's
 * `tiktok_too_many_photos` mirrors the count pre-flight.
 */
export function validateTikTokPhotoUrls(photoUrls: string[]): string | null {
  if (photoUrls.length === 0) {
    return 'TikTok photo posts require at least one image';
  }
  if (photoUrls.length > TIKTOK_PHOTO_MAX_IMAGES) {
    return `TikTok photo posts support up to ${TIKTOK_PHOTO_MAX_IMAGES} images`;
  }
  return null;
}
